import React, { FC, useCallback, useMemo } from 'react';
import { useAppDispatch } from 'src/app/hooks';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import {
  CancelPoPermissions,
  CompleteReceiptPermissions,
  DeleteReceiptPermissions,
} from 'src/components/guards/PermissionsList';
import useBoolean from 'src/hooks/useBoolean';
import useConfirm from 'src/hooks/useConfirm';
import usePermission from 'src/hooks/usePermissions';
import useCancelPoApi from 'src/services/useCancelPoApi';
import useDeleteReceiptsApi from 'src/services/useDeleteReceiptApi';
import useUnDeleteReceiptsApi from 'src/services/useUnDeleteReceiptApi';
import { IReceiptListItem } from 'src/types/Receipt';
import { Complete, Reopen } from '../../features/ViewReceiptsSlice';

interface Props {
  selectedRows: IReceiptListItem[];
  setRows: React.Dispatch<React.SetStateAction<IReceiptListItem[]>>;
  refresh: VoidFunction;
}

const getContents = (
  selectedRows: IReceiptListItem[],
  invalidIds: string[] | number[],
  text: string
) => {
  if (!selectedRows.length) {
    return [`Please select one or more records to ${text}`];
  }

  if (invalidIds.length) {
    return invalidIds.map((invalidId) => `# ${invalidId || ''}`);
  }

  return [];
};

const ActionButtons: FC<Props> = ({ selectedRows, setRows, refresh }) => {
  const { confirm } = useConfirm();
  const { deleteReceipts, deleteReceiptsLoading } = useDeleteReceiptsApi();
  const { unDeleteReceipts, unDeleteReceiptLoading } = useUnDeleteReceiptsApi();
  const { cancelPo, cancelPoCallLoading } = useCancelPoApi();
  const dispatch = useAppDispatch();

  const selectedReceiptIds = useMemo(
    () => selectedRows.map((row: IReceiptListItem) => row.receiptId),
    [selectedRows]
  );

  const hasCompleteReceiptPermission = usePermission({
    permissions: CompleteReceiptPermissions,
    or: true,
  });

  const hasDeleteReceiptPermission = usePermission({
    permissions: DeleteReceiptPermissions,
    or: true,
  });

  const hasReopenPermission = usePermission({
    permissions: CompleteReceiptPermissions,
    or: true,
  });

  const hasUnDeletePermission = usePermission({
    permissions: DeleteReceiptPermissions,
    or: true,
  });

  const hasCancelPoPermission = usePermission({
    permissions: CancelPoPermissions,
    or: true,
  });

  const {
    isDeleteDisabled,
    isCompleteDisabled,
    isReopenReceiptDisabled,
    isUndeleteDisabled,
    isCancelPODisabled,
  } = useMemo(() => {
    if (selectedRows.length === 0) {
      return {
        isDeleteDisabled: true,
        isCompleteDisabled: true,
        isReopenReceiptDisabled: true,
        isUndeleteDisabled: true,
        isCancelPODisabled: true,
      };
    }

    const isDeleteDisabled = !selectedRows.every((row) => row.canDelete);
    const isCompleteDisabled = !selectedRows.every((row) => row.canComplete);
    const isReopenReceiptDisabled = !selectedRows.every((row) => row.canReOpen);
    const isUndeleteDisabled = !selectedRows.every((row) => row.canUndelete);
    const isCancelPODisabled = !selectedRows.every((row) => row.isPOCancellable);

    return {
      isDeleteDisabled,
      isCompleteDisabled,
      isReopenReceiptDisabled,
      isUndeleteDisabled,
      isCancelPODisabled,
    };
  }, [selectedRows]);

  const {
    deleteInvalidIds,
    completeInvalidIds,
    reopenInvalidIds,
    undeleteInvalidIds,
    cancelInvalidIds,
  } = useMemo(() => {
    const deleteInvalidIds = selectedRows
      .filter((row) => !row.canDelete)
      .map((row) => row.receiptId);
    const completeInvalidIds = selectedRows
      .filter((row) => !row.canComplete)
      .map((row) => row.receiptId);
    const reopenInvalidIds = selectedRows
      .filter((row) => !row.canReOpen)
      .map((row) => row.receiptId);
    const undeleteInvalidIds = selectedRows
      .filter((row) => !row.canUndelete)
      .map((row) => row.receiptId);
    const cancelInvalidIds = selectedRows
      .filter((row) => !row.isPOCancellable)
      .map((row) => row.poNum);

    return {
      deleteInvalidIds,
      completeInvalidIds,
      reopenInvalidIds,
      undeleteInvalidIds,
      cancelInvalidIds,
    };
  }, [selectedRows]);

  const refreshList = useCallback(() => {
    refresh();
    setRows((prev) => prev.map((row) => ({ ...row, selected: false })));
  }, [refresh, setRows]);

  const {
    value: isCompleteReceipt,
    setTrue: enableIsCompleteReceipt,
    setFalse: disableIsCompleteReceipt,
  } = useBoolean();
  const {
    value: isReopenReceipt,
    setTrue: enableIsReopenReceipt,
    setFalse: disableIsReopenReceipt,
  } = useBoolean();

  const handleCompleteDocument = useCallback(async () => {
    try {
      enableIsCompleteReceipt();
      await dispatch(Complete(selectedReceiptIds));
    } catch (error) {
      console.error(error);
    } finally {
      refreshList();
      disableIsCompleteReceipt();
    }
  }, [
    disableIsCompleteReceipt,
    dispatch,
    enableIsCompleteReceipt,
    refreshList,
    selectedReceiptIds,
  ]);

  const handleDelete = useCallback(async () => {
    const isDeleteAccepted = await confirm({
      title: 'Delete Receipt(s)',
      content: <div>Are you sure you want to Delete the selected receipt(s)?</div>,
      confirmButtonText: 'OK',
    });
    if (isDeleteAccepted) {
      try {
        await deleteReceipts({ receiptIds: selectedReceiptIds });
      } catch (error) {
        console.error(error);
      } finally {
        refreshList();
      }
    }
  }, [confirm, deleteReceipts, refreshList, selectedReceiptIds]);

  const handleUndelete = useCallback(async () => {
    try {
      await unDeleteReceipts({ receiptIds: selectedReceiptIds });
    } catch (error) {
      console.error(error);
    } finally {
      refreshList();
    }
  }, [refreshList, selectedReceiptIds, unDeleteReceipts]);

  const handleCancelPO = useCallback(async () => {
    try {
      await cancelPo({ poIds: selectedRows.map((row) => row.poId) });
    } catch (error) {
      console.error(error);
    } finally {
      refreshList();
    }
  }, [cancelPo, refreshList, selectedRows]);

  const handleReopenReceipt = useCallback(async () => {
    try {
      enableIsReopenReceipt();
      await dispatch(Reopen(selectedReceiptIds));
    } catch (error) {
      console.error(error);
    } finally {
      disableIsReopenReceipt();
      refreshList();
    }
  }, [disableIsReopenReceipt, dispatch, enableIsReopenReceipt, refreshList, selectedReceiptIds]);

  const buttons = useMemo(
    () =>
      [
        {
          text: 'Complete Document',
          onClickFn: handleCompleteDocument,
          color: 'primary',
          disabled: isCompleteDisabled,
          hasPermission: hasCompleteReceiptPermission,
          invalidIds: completeInvalidIds,
          isLoading: isCompleteReceipt,
        },
        {
          text: 'Delete',
          onClickFn: handleDelete,
          color: 'error',
          disabled: isDeleteDisabled,
          hasPermission: hasDeleteReceiptPermission,
          invalidIds: deleteInvalidIds,
          isLoading: deleteReceiptsLoading,
        },
        {
          text: 'Undelete',
          onClickFn: handleUndelete,
          color: 'dark',
          disabled: isUndeleteDisabled,
          hasPermission: hasUnDeletePermission,
          invalidIds: undeleteInvalidIds,
          isLoading: unDeleteReceiptLoading,
        },
        {
          text: 'Cancel PO',
          onClickFn: handleCancelPO,
          color: 'secondary',
          disabled: isCancelPODisabled,
          hasPermission: hasCancelPoPermission,
          invalidIds: cancelInvalidIds,
          isLoading: cancelPoCallLoading,
        },
        {
          text: 'Re-Open',
          onClickFn: handleReopenReceipt,
          color: 'dark',
          disabled: isReopenReceiptDisabled,
          hasPermission: hasReopenPermission,
          invalidIds: reopenInvalidIds,
          isLoading: isReopenReceipt,
        },
      ] as const,
    [
      handleCompleteDocument,
      isCompleteDisabled,
      hasCompleteReceiptPermission,
      completeInvalidIds,
      isCompleteReceipt,
      handleDelete,
      isDeleteDisabled,
      hasDeleteReceiptPermission,
      deleteInvalidIds,
      deleteReceiptsLoading,
      handleUndelete,
      isUndeleteDisabled,
      hasUnDeletePermission,
      undeleteInvalidIds,
      unDeleteReceiptLoading,
      handleCancelPO,
      isCancelPODisabled,
      hasCancelPoPermission,
      cancelInvalidIds,
      cancelPoCallLoading,
      handleReopenReceipt,
      isReopenReceiptDisabled,
      hasReopenPermission,
      reopenInvalidIds,
      isReopenReceipt,
    ]
  );

  return (
    <>
      {buttons.map((btn) => (
        <div key={btn.text}>
          {btn.hasPermission && (
            <PopUp
              content={
                <PopUpContentWrapper
                  contents={getContents(selectedRows, btn.invalidIds, btn.text)}
                  type={selectedRows.length ? 'Error' : 'Information'}
                  prefix={selectedRows.length ? btn.text : undefined}
                  title={
                    selectedRows.length
                      ? `Documents below are ineligible to ${btn.text}`
                      : undefined
                  }
                />
              }
              show={btn.disabled}
            >
              <Button
                disabled={btn.disabled}
                loading={btn.isLoading}
                color={btn.color}
                onClick={btn.onClickFn}
              >
                {btn.text}
              </Button>
            </PopUp>
          )}
        </div>
      ))}
    </>
  );
};

export default ActionButtons;
